import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, Link } from "react-router-dom";
import {
  Button,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  InputAdornment,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const PANELS = [
  {
    id: "profile",
    label: "Update Profile",
    description: "Update your account information",
    Icon: PersonIcon,
  },
  {
    id: "password",
    label: "Update Password",
    description: "Change your account password",
    Icon: LockIcon,
  },
];

// Both collapsed — resets every time the component mounts
const COLLAPSED_STATE = Object.fromEntries(PANELS.map((p) => [p.id, false]));

function ProfileSettings() {
  const navigate = useNavigate();

  const userInit = { name: "", email: "" };
  const passwordInit = { new_password: "", password_confirmation: "" };

  const [btnLoading, setBtnLoading] = useState(false);
  const [btnLoading2, setBtnLoading2] = useState(false);
  const [user, setProfile] = useState(userInit);
  const [password, setPassword] = useState(passwordInit);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Start fully collapsed, reset on every mount ──
  const [expanded, setExpanded] = useState(COLLAPSED_STATE);
  const [activePanel, setActivePanel] = useState(null);

  const panelRefs = useRef({});

  const allExpanded = PANELS.every((p) => expanded[p.id]);

  // ── Exclusive: only one panel open at a time ──
  const openOnly = (id) =>
    setExpanded(Object.fromEntries(PANELS.map((p) => [p.id, p.id === id])));

  // Left menu click → open exclusively + scroll
  const handleMenuClick = (id) => {
    setActivePanel(id);
    openOnly(id);
    setTimeout(() => {
      panelRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120); 
  };

  // Accordion chevron click → exclusive toggle, sync menu highlight
  const handleAccordionToggle = (id) => {
    const opening = !expanded[id];
    if (opening) {
      setActivePanel(id);
      openOnly(id);
    } else {
      setExpanded((prev) => ({ ...prev, [id]: false }));
      if (activePanel === id) setActivePanel(null);
    }
  };

  // Expand all — keeps active on first panel
  const expandAll = () => {
    setExpanded(Object.fromEntries(PANELS.map((p) => [p.id, true])));
    setActivePanel(PANELS[0].id);
  };

  // Collapse all — clears active
  const collapseAll = () => {
    setExpanded(COLLAPSED_STATE);
    setActivePanel(null);
  };

  // ── Data ──
  const getUser = useCallback(() => {
    axios
      .get(
        `${Helpers.baseUrl}/users/get-profile/${Helpers.authUser._id}`,
        Helpers.headers,
      )
      .then((res) => {
        setProfile(res.data);
        setPassword((prev) => ({ ...prev, _id: res.data._id }));
      })
      .catch((err) => Helpers.unauthenticated(err, navigate));
  }, [navigate]);

  const handleSaveProfile = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    setErrors({});
    axios
      .post(`${Helpers.baseUrl}/users/update-profile`, user, Helpers.headers)
      .then(() => {
        getUser();
        Helpers.toast("success", "Profile updated successfully");
        setBtnLoading(false);
      })
      .catch((err) => {
        Helpers.toast("error", Helpers.error_response(err));
        setErrors(err.response?.data?.errors || {});
        setBtnLoading(false);
      });
  };

  const handleSavePassword = (e) => {
    e.preventDefault();
    setBtnLoading2(true);
    setErrors({});
    axios
      .post(
        `${Helpers.baseUrl}/users/update-password`,
        password,
        Helpers.headers,
      )
      .then(() => {
        setPassword({ ...passwordInit, _id: password._id });
        Helpers.toast(
          "success",
          "Password updated successfully. Logging out...",
        );
        setBtnLoading2(false);
        setTimeout(() => {
          localStorage.removeItem("authUser");
          localStorage.removeItem("permissions");
          navigate("/login");
        }, 1500);
      })
      .catch((err) => {
        Helpers.toast("error", Helpers.error_response(err));
        setErrors(err.response?.data?.errors || {});
        setBtnLoading2(false);
      });
  };

  useEffect(() => {
    getUser();
    setExpanded(COLLAPSED_STATE);
    setActivePanel(null);
  }, [getUser]);

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: "64px",
        minHeight: "100vh",
        overflowY: "auto",
        boxSizing: "border-box",
        transition: "width 0.25s ease",
        willChange: "width",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          transition: "padding 0.25s ease",
          boxSizing: "border-box",
        }}
      >
        {/* ── Banner — identical to ModuleLayout's header Paper ── */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255,255,255,0.05)",
              borderRadius: "50%",
              transform: "translate(30%,-30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255,255,255,0.9)",
                fontSize: "0.875rem",
                "&:hover": { color: "white" },
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Home
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Profile Settings
            </Typography>
          </Breadcrumbs>

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 0.5,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Profile Settings
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
            >
              Manage your account settings and preferences
            </Typography>
          </Box>
        </Paper>

        {/* ── Single Card wrapping both columns ── */}
        <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* ── Card Header: Settings label + Expand All button side by side ── */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background:
                "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
              borderBottom: "1px solid #e9ecf0",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "#667eea",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Settings
            </Typography>

            <Tooltip title={allExpanded ? "Collapse all" : "Expand all"} arrow>
              <Button
                size="small"
                variant="outlined"
                onClick={allExpanded ? collapseAll : expandAll}
                startIcon={
                  allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />
                }
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  borderColor: "#667eea50",
                  color: "#667eea",
                  "&:hover": {
                    borderColor: "#667eea",
                    background: "rgba(102,126,234,0.07)",
                  },
                }}
              >
                {allExpanded ? "Collapse All" : "Expand All"}
              </Button>
            </Tooltip>
          </Box>

          {/* ── Body: left menu + right accordions side by side ── */}
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            {/* Left menu */}
            <Box
              sx={{
                width: 240,
                flexShrink: 0,
                borderRight: "1px solid #e9ecf0",
                position: "sticky",
                top: 76,
                alignSelf: "flex-start",
              }}
            >
              <List disablePadding sx={{ py: 1 }}>
                {PANELS.map(({ id, label, description, Icon }) => {
                  const isActive = activePanel === id;
                  return (
                    <ListItemButton
                      key={id}
                      onClick={() => handleMenuClick(id)}
                      sx={{
                        mx: 1,
                        mb: 0.5,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1,
                        backgroundColor: isActive
                          ? "rgba(102,126,234,0.1)"
                          : "transparent",
                        borderLeft: isActive
                          ? "3px solid #667eea"
                          : "3px solid transparent",
                        transition: "all 0.18s ease",
                        "&:hover": {
                          backgroundColor: "rgba(102,126,234,0.07)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon
                          sx={{
                            fontSize: 20,
                            color: isActive ? "#667eea" : "#98a2b3",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        secondary={description}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? "#667eea" : "#344054",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.72rem",
                          color: "#98a2b3",
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            {/* Right accordions */}
            <Box sx={{ flex: 1, minWidth: 0, p: 2.5 }}>
              <div ref={(el) => (panelRefs.current["profile"] = el)}>
                <Accordion
                  expanded={expanded.profile}
                  onChange={() => handleAccordionToggle("profile")}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    boxShadow: expanded.profile ? 3 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: expanded.profile ? "#f8f9fa" : "white",
                      borderRadius: expanded.profile ? "8px 8px 0 0" : 2,
                      "&:hover": { bgcolor: "#f8f9fa" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <PersonIcon sx={{ color: "#667eea", fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Update Profile
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Update your account information
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <form onSubmit={handleSaveProfile}>
                      <Stack spacing={3}>
                        <TextField
                          label="User Name"
                          fullWidth
                          value={user.name}
                          onChange={(e) =>
                            setProfile({ ...user, name: e.target.value })
                          }
                          error={Boolean(errors.name)}
                          helperText={errors.name || ""}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Email"
                          type="email"
                          fullWidth
                          value={user.email}
                          onChange={(e) =>
                            setProfile({ ...user, email: e.target.value })
                          }
                          error={Boolean(errors.email)}
                          helperText={errors.email || ""}
                        />
                        <Divider />
                        <Box>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={btnLoading}
                            sx={{
                              textTransform: "none",
                              px: 4,
                              backgroundColor: "#28a745",
                              "&:hover": { backgroundColor: "#218838" },
                            }}
                          >
                            {btnLoading ? "Saving..." : "Save Profile"}
                          </Button>
                        </Box>
                      </Stack>
                    </form>
                  </AccordionDetails>
                </Accordion>
              </div>

              <div
                ref={(el) => (panelRefs.current["password"] = el)}
                style={{ marginTop: "5px" }}
              >
                <Accordion
                  expanded={expanded.password}
                  onChange={() => handleAccordionToggle("password")}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    boxShadow: expanded.password ? 3 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: expanded.password ? "#f8f9fa" : "white",
                      borderRadius: expanded.password ? "8px 8px 0 0" : 2,
                      "&:hover": { bgcolor: "#f8f9fa" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <LockIcon sx={{ color: "#667eea", fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Update Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Change your account password
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <form onSubmit={handleSavePassword}>
                      <Stack spacing={3}>
                        <TextField
                          label="New Password"
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          value={password.new_password}
                          onChange={(e) =>
                            setPassword({
                              ...password,
                              new_password: e.target.value,
                            })
                          }
                          error={Boolean(errors.new_password)}
                          helperText={errors.new_password || ""}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Confirm New Password"
                          type={showConfirmPassword ? "text" : "password"}
                          fullWidth
                          value={password.password_confirmation}
                          onChange={(e) =>
                            setPassword({
                              ...password,
                              password_confirmation: e.target.value,
                            })
                          }
                          error={Boolean(errors.password_confirmation)}
                          helperText={errors.password_confirmation || ""}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  edge="end"
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Divider />
                        <Box>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={btnLoading2}
                            sx={{
                              textTransform: "none",
                              px: 4,
                              backgroundColor: "#28a745",
                              "&:hover": { backgroundColor: "#218838" },
                            }}
                          >
                            {btnLoading2 ? "Updating..." : "Update Password"}
                          </Button>
                        </Box>
                      </Stack>
                    </form>
                  </AccordionDetails>
                </Accordion>
              </div>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ProfileSettings;
